import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Coffee, Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { DonationForm } from "@/components/DonationForm";
import { useSiteContent } from "@/hooks/useSiteContent";

const WomensMinistry = () => {
  const { t } = useLanguage();
  const { content, isLoading } = useSiteContent('womens_ministry');
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
              <Coffee className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
              {content.title || "Women's Ministry"}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {content.subtitle || "A place for women to grow in faith, fellowship, and purpose"}
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-12">
            <section className="bg-card rounded-lg p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-foreground mb-6">{content.about_title || "About Women's Ministry"}</h2>
              <p className="text-muted-foreground mb-4">
                {content.about_text1 || "Our Women's Ministry provides a welcoming space for women of all ages to connect, grow in their faith, and support one another."}
              </p>
              <p className="text-muted-foreground">
                {content.about_text2 || "Through Bible studies, prayer meetings, and fellowship events, we strengthen relationships with God and each other."}
              </p>
            </section>

            {content.gatherings_title && (
              <section className="bg-card rounded-lg p-8 shadow-lg">
                <h2 className="text-3xl font-bold text-foreground mb-6">{content.gatherings_title}</h2>
                <div className="space-y-6">
                  {content.biblestudy_title && (
                    <div className="border-l-4 border-primary pl-4">
                      <h3 className="font-bold text-xl mb-2">{content.biblestudy_title}</h3>
                      <div className="flex items-center gap-4 text-muted-foreground mb-2">
                        {content.biblestudy_day && (
                          <span className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" /> {content.biblestudy_day}
                          </span>
                        )}
                        {content.biblestudy_time && (
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4" /> {content.biblestudy_time}
                          </span>
                        )}
                      </div>
                      {content.biblestudy_desc && (
                        <p className="text-muted-foreground">
                          {content.biblestudy_desc}
                        </p>
                      )}
                    </div>
                  )}
                  {content.prayer_title && (
                    <div className="border-l-4 border-primary pl-4">
                      <h3 className="font-bold text-xl mb-2">{content.prayer_title}</h3>
                      <div className="flex items-center gap-4 text-muted-foreground mb-2">
                        {content.prayer_day && (
                          <span className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" /> {content.prayer_day}
                          </span>
                        )}
                        {content.prayer_time && (
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4" /> {content.prayer_time}
                          </span>
                        )}
                      </div>
                      {content.prayer_desc && (
                        <p className="text-muted-foreground">
                          {content.prayer_desc}
                        </p>
                      )}
                    </div>
                  )}
                  {content.brunch_title && (
                    <div className="border-l-4 border-primary pl-4">
                      <h3 className="font-bold text-xl mb-2">{content.brunch_title}</h3>
                      <div className="flex items-center gap-4 text-muted-foreground mb-2">
                        {content.brunch_day && (
                          <span className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" /> {content.brunch_day}
                          </span>
                        )}
                        {content.brunch_time && (
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4" /> {content.brunch_time}
                          </span>
                        )}
                      </div>
                      {content.brunch_desc && (
                        <p className="text-muted-foreground">
                          {content.brunch_desc}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </section>
            )}

            {content.events_title && (
              <section className="bg-card rounded-lg p-8 shadow-lg">
                <h2 className="text-3xl font-bold text-foreground mb-6">{content.events_title}</h2>
                <ul className="space-y-3 text-muted-foreground">
                  {content.event1_name && (
                    <li className="flex items-start gap-3">
                      <span className="text-primary font-bold">•</span>
                      <span><strong>{content.event1_name}:</strong> {content.event1_desc}</span>
                    </li>
                  )}
                  {content.event2_name && (
                    <li className="flex items-start gap-3">
                      <span className="text-primary font-bold">•</span>
                      <span><strong>{content.event2_name}:</strong> {content.event2_desc}</span>
                    </li>
                  )}
                  {content.event3_name && (
                    <li className="flex items-start gap-3">
                      <span className="text-primary font-bold">•</span>
                      <span><strong>{content.event3_name}:</strong> {content.event3_desc}</span>
                    </li>
                  )}
                  {content.event4_name && (
                    <li className="flex items-start gap-3">
                      <span className="text-primary font-bold">•</span>
                      <span><strong>{content.event4_name}:</strong> {content.event4_desc}</span>
                    </li>
                  )}
                </ul>
              </section>
            )}

            <section className="bg-card rounded-lg p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-foreground mb-6">{content.connect_title || "Get Involved"}</h2>
              <p className="text-muted-foreground mb-6">
                {content.connect_text || "Interested in joining our Women's Ministry? We'd love to connect with you!"}
              </p>
              <Button size="lg" onClick={() => window.location.href = "/#contact"}>
                {content.contact_button || "Contact Us"}
              </Button>
            </section>

            <section className="bg-card rounded-lg p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-foreground mb-6">Support Our Ministry</h2>
              <p className="text-muted-foreground mb-6">
                Your donations help fund events, retreats, and resources for women's ministry.
              </p>
              <DonationForm />
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WomensMinistry;
